/**
 * /settings/imports — M29 PR 9.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../../components/Loading";
import { EmptyState, PageHeader } from "../../components/ui";
import { api } from "../../lib/api";
import type { ImportPendingRowDTO, ImportSessionDTO } from "../../lib/api";

type Tab = "history" | "pending" | "templates";

export function ImportsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("history");

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-6">
      <PageHeader
        title={t("app.settings_imports_title")}
        subtitle={t("app.imp_subtitle")}
        breadcrumbs={[
          { label: t("app.settings_breadcrumb"), href: "/settings" },
          { label: t("app.settings_imports_title") },
        ]}
      />

      <nav className="flex gap-1 border-b border-border-subtle">
        <_TabBtn active={tab === "history"} onClick={() => setTab("history")}>
          {t("app.imp_tab_history")}
        </_TabBtn>
        <_TabBtn active={tab === "pending"} onClick={() => setTab("pending")}>
          {t("app.imp_tab_pending")}
        </_TabBtn>
        <_TabBtn active={tab === "templates"} onClick={() => setTab("templates")}>
          {t("app.imp_tab_templates")}
        </_TabBtn>
      </nav>

      {tab === "history" && <_HistoryTab />}
      {tab === "pending" && <_PendingTab />}
      {tab === "templates" && <_TemplatesTab />}
    </div>
  );
}

function _TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 text-sm font-medium transition-colors ${
        active
          ? "border-b-2 border-brand-blue text-brand-deep"
          : "text-text-tertiary hover:text-text-primary"
      }`}
    >
      {children}
    </button>
  );
}

function _HistoryTab() {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["import-sessions"],
    queryFn: () => api.importListSessions(),
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorPanel message={(q.error as Error).message} />;
  const sessions = q.data ?? [];
  if (sessions.length === 0) {
    return (
      <EmptyState
        headline={t("app.imp_no_past_imports")}
        description={t("app.imp_no_past_imports_body")}
      />
    );
  }
  return (
    <table className="w-full text-sm">
      <thead className="text-xs uppercase tracking-wider text-text-tertiary">
        <tr>
          <th className="text-start py-2">{t("app.imp_col_uploaded")}</th>
          <th className="text-start py-2">{t("app.imp_col_files")}</th>
          <th className="text-start py-2">{t("app.imp_col_rows")}</th>
          <th className="text-start py-2">{t("app.imp_col_status")}</th>
          <th className="text-start py-2">{t("app.imp_col_committed")}</th>
          <th className="text-start py-2">{t("app.imp_col_pending")}</th>
        </tr>
      </thead>
      <tbody>
        {sessions.map((s) => (
          <_SessionRow key={s.id} session={s} />
        ))}
      </tbody>
    </table>
  );
}

function _SessionRow({ session }: { session: ImportSessionDTO }) {
  return (
    <tr className="border-t border-border-subtle">
      <td className="py-2.5 pe-3">
        <Link
          to={`/operations/import?session_id=${session.id}`}
          className="text-brand-blue hover:underline"
        >
          {new Date(session.uploaded_at).toLocaleString()}
        </Link>
      </td>
      <td className="py-2.5 pe-3">{session.total_files}</td>
      <td className="py-2.5 pe-3">{session.total_rows_parsed}</td>
      <td className="py-2.5 pe-3">
        <_StatusChip status={session.status} />
      </td>
      <td className="py-2.5 pe-3 text-emerald-700">
        {session.total_rows_committed}
      </td>
      <td className="py-2.5 text-amber-700">
        {session.total_rows_pending}
      </td>
    </tr>
  );
}

function _StatusChip({ status }: { status: string }) {
  const cls: Record<string, string> = {
    completed: "bg-emerald-100 text-emerald-800",
    committing: "bg-brand-blue/20 text-brand-blue",
    reviewing_proposal: "bg-amber-100 text-amber-800",
    awaiting_clarification: "bg-amber-100 text-amber-800",
    understanding: "bg-brand-blue/20 text-brand-blue",
    uploaded: "bg-surface-sunken text-text-secondary",
    failed: "bg-rose-100 text-rose-800",
    abandoned: "bg-surface-sunken text-text-tertiary",
  };
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${
        cls[status] ?? "bg-surface-sunken text-text-secondary"
      }`}
    >
      {status.replace("_", " ")}
    </span>
  );
}

function _PendingTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["import-pending-all"],
    queryFn: () => api.importListAllPending(),
  });
  const discard = useMutation({
    mutationFn: (id: string) => api.importDiscardPending(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["import-pending-all"] }),
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorPanel message={(q.error as Error).message} />;
  const rows = q.data ?? [];
  if (rows.length === 0) {
    return (
      <EmptyState
        headline={t("app.imp_pending_empty")}
        description={t("app.imp_pending_empty_body")}
      />
    );
  }
  return (
    <ul className="divide-y divide-border-subtle">
      {rows.map((r) => (
        <_PendingRow
          key={r.id}
          row={r}
          onDiscard={() => discard.mutate(r.id)}
        />
      ))}
    </ul>
  );
}

function _PendingRow({
  row,
  onDiscard,
}: {
  row: ImportPendingRowDTO;
  onDiscard: () => void;
}) {
  const { t } = useTranslation();
  const daysLeft = Math.max(
    0,
    Math.ceil(
      (new Date(row.expires_at).getTime() - Date.now()) /
        (1000 * 60 * 60 * 24),
    ),
  );
  const expireTone =
    daysLeft <= 2
      ? "text-rose-700"
      : daysLeft <= 9
      ? "text-amber-700"
      : "text-text-tertiary";
  return (
    <li className="flex flex-wrap items-start justify-between gap-3 py-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 text-xs">
          <span className="rounded bg-surface-sunken px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-text-secondary">
            {row.entity_type}
          </span>
          <Link
            to={`/operations/import?session_id=${row.import_session_id}`}
            className="text-brand-blue hover:underline"
          >
            {t("app.imp_view_session")}
          </Link>
        </div>
        <pre className="mt-1 overflow-x-auto text-[10px] font-mono text-text-tertiary">
          {JSON.stringify(row.mapped_data, null, 2).slice(0, 240)}
          {JSON.stringify(row.mapped_data).length > 240 ? "…" : ""}
        </pre>
        {row.validation_messages.length > 0 && (
          <ul className="mt-1 space-y-0.5 text-xs text-rose-700">
            {row.validation_messages.slice(0, 3).map((m, i) => (
              <li key={i} className="flex items-start gap-1">
                <AlertTriangle size={10} className="mt-0.5 shrink-0" />
                <span>{m.message_en}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="flex flex-col items-end gap-2 text-xs">
        <span className={expireTone}>
          {t(
            daysLeft === 1 ? "app.imp_expires_in_one" : "app.imp_expires_in_many",
            { n: daysLeft },
          )}
        </span>
        <button
          type="button"
          onClick={onDiscard}
          className="inline-flex items-center gap-1 rounded border border-rose-300 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50"
        >
          <Trash2 size={10} /> {t("app.imp_discard")}
        </button>
      </div>
    </li>
  );
}

function _TemplatesTab() {
  const { t } = useTranslation();
  return (
    <EmptyState
      headline={t("app.imp_tpl_title")}
      description={t("app.imp_tpl_body")}
    />
  );
}
